/* eslint-disable @typescript-eslint/no-explicit-any */
import { GetWorkoutByIdQuery } from 'generated/graphql';

import * as enums from '@playbook-technologies/common-enums';

import {
  TabataDTO,
  TabataExerciseDTO,
  ServerSideSet,
  ServerExerciseSingle,
  TabataExerciseSetDTO,
  HIITDTO,
  HIITExerciseDTO,
  HIITExerciseSetDTO,
  ServerExerciseAutoplayed,
} from './workout.domain';
import { LegacyHIIT, LegacyTabata } from '../../workout/models/workout.domain';
import { v4 } from 'uuid';
import { defaults } from '../view-model/workout.schema';

const filterExercises = (w: GetWorkoutByIdQuery['workout'], type: 10 | 11) =>
  (w?.exercises || []).filter(
    (e) => (e as any)?.type === type,
  ) as ServerExerciseAutoplayed[];

export const toExerciseDTOs = (w: GetWorkoutByIdQuery['workout']) => [
  ...filterExercises(w, 10).map(toTabataDTO),
  ...filterExercises(w, 11).map(toHIITDTO),
];

// Legacy autoplayed items (clipboard, form values) carry the same shape the
// server returns - see toLegacyAutoplay.
export const toAutoplayedDTO = (
  item: LegacyHIIT | LegacyTabata,
): TabataDTO | HIITDTO => {
  const e = item as unknown as ServerExerciseAutoplayed;
  return item.type === 11 ? toHIITDTO(e) : toTabataDTO(e);
};

const toHIITDTO = (c: ServerExerciseAutoplayed): HIITDTO => {
  const exercises = c.exercises
    ?.filter(isServerExerciseSingle)
    .map(toHIITExerciseDTO) as HIITExerciseDTO[];

  return {
    id: c.id || v4(),
    name: c.name || '',
    longRest: c.longRest || defaults.longRest.default,
    type: 'HIIT',
    rounds: c.rounds || defaults.rounds.default,
    description: c.description || '',
    goal: 'time',
    exercises,
  };
};

const toHIITExerciseDTO = (e: ServerExerciseSingle): HIITExerciseDTO => ({
  id: (e as any).id || v4(),
  name: e.name,
  type: 'HIITExercise',
  description: e.description || '',
  sets: toHIITExerciseSets((e.sets as any) || []),
  media: e.media || null,
});

const toHIITExerciseSets = (
  sets: (ServerSideSet | null)[],
): HIITExerciseSetDTO[] =>
  (sets.filter((s) => s !== null) as ServerSideSet[]).map(
    (s): HIITExerciseSetDTO => ({
      time: s.time || defaults.time.default,
      rest: s.rest ?? defaults.rest.default,
      rep: toRep(s),
    }),
  );

const isServerExerciseSingle = (e: any): e is ServerExerciseSingle =>
  (e as ServerExerciseSingle).type === enums.Exercise.Type.Single.value;

const toTabataDTO = (c: ServerExerciseAutoplayed): TabataDTO => {
  const exercises = c.exercises
    ?.filter(isServerExerciseSingle)
    .map(toTabataExerciseDTO) as TabataExerciseDTO[];

  return {
    id: c.id || v4(),
    type: 'Tabata',
    name: c.name || '',
    longRest: c.longRest || defaults.longRest.default,
    rounds: c.rounds || defaults.rounds.default,
    description: c.description || '',
    exercises,
  };
};

const toTabataExerciseDTO = (e: ServerExerciseSingle): TabataExerciseDTO => ({
  id: (e as any).id || v4(),
  name: e.name,
  type: 'TabataExercise',

  description: e.description || '',
  sets: toTabataExerciseSets((e.sets as any) || []),
  media: e.media || null,
});

const toTabataExerciseSets = (
  sets: (ServerSideSet | null)[],
): TabataExerciseSetDTO[] => {
  const res = (sets.filter((s) => s !== null) as ServerSideSet[]).map(
    (s): TabataExerciseSetDTO => {
      return {
        time: s.time || defaults.time.default,
        rest: s.rest ?? defaults.rest.default,
        rep: toRep(s),
      };
    },
  );
  return res;
};

// creator-api returns timed HIIT/Tabata sets as mode fixed with this amount
// (hotfix for creator iOS dropping time on toFailure), so the amount still
// marks AMRAP here.
const LEGACY_TO_FAILURE_AMOUNT = 619;

const toRep = (s: ServerSideSet): TabataExerciseSetDTO['rep'] => {
  if (s.mode === 'toFailure') return { type: 'AMRAP' };
  const r = s.reps as [number, string] | string | number;
  if (typeof r === 'string')
    return { type: 'fixed', value: parseInt(r, 10) || defaults.reps.default };
  if (typeof r === 'number') return { type: 'fixed', value: r };
  const [value] = r;
  if (Number(value) === LEGACY_TO_FAILURE_AMOUNT) return { type: 'AMRAP' };
  return {
    type: 'fixed',
    value: parseInt(`${value}`, 10) || defaults.reps.default,
  };
};
